//May 8 FieldMetadataContext:
//mar21 FIeldMetadataContext
//Mar 4 FieldMetadataContext without headerfield component fixes
import React, { createContext, useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { getFieldMetadata, getDefaultFieldMetadata, getFieldMetadataForSingleField } from '../utils/firestore'; 
import { useFormContext } from './FormContext'; // Provides formId
import { db } from '../firebase';
import { getDoc, doc } from 'firebase/firestore'; 
import { safeSetItem, safeGetItem, safeRemoveItem } from '../utils/storageUtils'; // Add safeRemoveItem
import { decompressData } from '../utils/decompressionUtils'; 
import { v4 as uuidv4 } from 'uuid';

import { resolveFieldsMetadata, getFormVersionSnapshot } from '../utils/formVersionResolver';
import {
  areAssetReferencesEqual,
  normalizeImageFieldMetadata,
} from '../utils/assetFieldMetadata';

const FieldMetadataContext = createContext();

export const useFieldMetadata = () => useContext(FieldMetadataContext);

export const FieldMetadataProvider = ({ children }) => {
    const { formId, formIdReady } = useFormContext();
    
// Initialize metadata with stored data if available
// NOTE: metadataKey/effectiveViewMode are not available yet in this initializer,
// so we compute the same effective mode inline (matches FormLayoutContext policy).
const [fieldsMetadata, setFieldsMetadata] = useState(() => {
  if (!formId) return {};

  try {
    const isBuilder = (typeof window !== 'undefined') && window.location.pathname.startsWith('/form-builder');

    let mode = 'published';
    if (isBuilder) {
      const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
      const parsed = raw ? JSON.parse(raw) : null;
      mode = parsed?.value || 'auto';
    }

    const modeKey = `form_${formId}_fieldsMetadata__${mode}`;

    // Prefer mode-aware cache; fallback to legacy
    return safeGetItem(modeKey) || safeGetItem(`form_${formId}_fieldsMetadata`) || {};
  } catch (e) {
    return safeGetItem(`form_${formId}_fieldsMetadata`) || {};
  }
});

    
// Track loading state and errors
const [isLoading, setIsLoading] = useState(() => {
  if (!formIdReady || !formId) return true;

  try {
    const isBuilder = (typeof window !== 'undefined') && window.location.pathname.startsWith('/form-builder');

    let mode = 'published';
    if (isBuilder) {
      const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
      const parsed = raw ? JSON.parse(raw) : null;
      mode = parsed?.value || 'auto';
    }

    const modeKey = `form_${formId}_fieldsMetadata__${mode}`;

    // Loading is false if either mode-aware cache OR legacy cache exists
    const hasCache = !!(safeGetItem(modeKey) || safeGetItem(`form_${formId}_fieldsMetadata`));
    return !hasCache;
  } catch (e) {
    return !safeGetItem(`form_${formId}_fieldsMetadata`);
  }
});

    const [error, setError] = useState(null);
    const [isNewFormCreation, setIsNewFormCreation] = useState(false);

    // ────────────────────────────────────────────────────────────────
    // Route-aware flags (builder vs viewer) – mirrors FormThemeContext
    // ────────────────────────────────────────────────────────────────
    const isPublicPreviewMode = useMemo(() => {
      try {
        const raw = sessionStorage.getItem('isPublicPreview');
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.value === true;
      } catch (e) {
        return false;
      }
    }, []);

    const isBuilderRoute = useMemo(() => {
      if (typeof window === 'undefined') return false;
      return window.location.pathname.startsWith('/form-builder');
    }, []);

    // ────────────────────────────────────────────────────────────────
    // Mode-aware metadata cache keys (aligned with FormLayoutContext)
    // - Builder route: honor the builder view mode (auto/published/draftOverlay)
    // - Viewer route (/forms/:id): ALWAYS force published
    // ────────────────────────────────────────────────────────────────
    const builderViewMode = useMemo(() => {
      try {
        if (!formId) return 'auto';
        const raw = sessionStorage.getItem(`form_${formId}_layoutVersionMode`);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.value || 'auto'; // 'auto' | 'published' | 'draftOverlay'
      } catch (e) {
        return 'auto';
      }
    }, [formId]);

    const effectiveViewMode = useMemo(() => {
      // Match FormLayoutContext policy: only /form-builder honors the toggle
      if (isBuilderRoute) return builderViewMode;
      return 'published';
    }, [isBuilderRoute, builderViewMode]);

    const metadataKey = useMemo(() => {
      if (!formId) return null;
      return `form_${formId}_fieldsMetadata__${effectiveViewMode}`;
    }, [formId, effectiveViewMode]);

    const lastUpdatedKey = useMemo(() => {
      if (!formId) return null;
      return `form_${formId}_metadataLastUpdated__${effectiveViewMode}`;
    }, [formId, effectiveViewMode]);

    // Legacy keys (pre-mode-aware). Keep for safe migration / fallback reads.
    const legacyMetadataKey = useMemo(() => {
      if (!formId) return null;
      return `form_${formId}_fieldsMetadata`;
    }, [formId]);

     const legacyLastUpdatedKey = useMemo(() => {
      if (!formId) return null;
      return `form_${formId}_metadataLastUpdated`;
    }, [formId]);

    const normalizeMetadataForContext = useCallback((metadata = {}) => {
      if (!metadata || typeof metadata !== 'object') return {};

      return Object.keys(metadata).reduce((acc, fieldId) => {
        const fieldData = { ...(metadata[fieldId] || {}) };

        // Ensure header fields have all required attributes
        if (fieldData.type === 'header') {
          fieldData.header = fieldData.header || "New Header Text";
          fieldData.enableSubheader =
            fieldData.enableSubheader !== undefined
              ? fieldData.enableSubheader
              : false;

          fieldData.subheader = fieldData.subheader || "";

          fieldData.headerStyle = {
            ...fieldData.headerStyle,
            fontSize: fieldData.headerStyle?.fontSize || "24px",
            headerColor: fieldData.headerStyle?.headerColor || "#000000",
            textAlign: fieldData.headerStyle?.textAlign || "center",
            fontWeight: fieldData.headerStyle?.fontWeight || "normal",
            margin: fieldData.headerStyle?.margin || "0px 0",
            padding: fieldData.headerStyle?.padding || "0px 0",
          };

          fieldData.subheaderStyle = {
            ...fieldData.subheaderStyle,
            subFontSize: fieldData.subheaderStyle?.subFontSize || "16px",
            subheaderColor: fieldData.subheaderStyle?.subheaderColor || "#666666",
            subheaderFontWeight:
              fieldData.subheaderStyle?.subheaderFontWeight || "normal",
            textAlign: fieldData.subheaderStyle?.textAlign || "center",
            margin: fieldData.subheaderStyle?.margin || "0px 0",
            padding: fieldData.subheaderStyle?.padding || "0px 0",
          };

          // Remove outdated `.style` property if present
          if (fieldData.style) {
            console.warn(
              `[FieldMetadataContext] Removing outdated 'style' property for header field ID: ${fieldId}`
            );
            delete fieldData.style;
          }
        }

        if (fieldData.type === 'image') {
          acc[fieldId] = normalizeImageFieldMetadata(fieldData);
          return acc;
        }

        acc[fieldId] = fieldData;
        return acc;
      }, {});
    }, []);

    /**
     * Helper function to skip submit buttons and avoid unnecessary UFI generation.
     * Checks if the field is of type 'submit' and if metadata already exists.
     * @param {string} fieldType 
     * @param {object} fieldMetadata 
     * @returns {boolean} Whether the field should be skipped or not.
     */
    const shouldSkipField = (fieldType, fieldMetadata) => {
        if (fieldType === 'submit') {
//            console.log("[FieldMetadataContext] Skipping submit button field from metadata handling.");
            return true;
        }
        if (fieldMetadata && fieldMetadata.ufi) {
//            console.log(`[FieldMetadataContext] Field metadata for UFI ${fieldMetadata.ufi} already exists. Skipping unnecessary updates.`);
            return true;
        }
        return false;
    };

    // NEW: Effect to clear stale field metadata on new form creation.
    // This effect checks localStorage for a flag "form_${formId}_isNew" that was set by FormContext.
    // If the flag is present and equals 'true', it will remove the stored fields metadata and reset the state,
    // then remove the flag so that subsequent renders use the new metadata.
    useEffect(() => {
      if (formIdReady && formId && isNewFormCreation) {
        const isNewFlag = localStorage.getItem(`form_${formId}_isNew`);
        if (isNewFlag === 'true') {
//          console.log(`[FieldMetadataContext] New form creation detected via localStorage. Removing any leftover metadata for form ${formId}.`);
          // Remove any leftover metadata across ALL modes + legacy key
          safeRemoveItem(`form_${formId}_fieldsMetadata`);
          safeRemoveItem(`form_${formId}_fieldsMetadata__auto`);
          safeRemoveItem(`form_${formId}_fieldsMetadata__published`);
          safeRemoveItem(`form_${formId}_fieldsMetadata__draftOverlay`);
          setFieldsMetadata({});
          localStorage.removeItem(`form_${formId}_isNew`);
        }
      }
    }, [formIdReady, formId, isNewFormCreation]);





/**
 * Fetch all metadata for a form's fields.
 * This checks local/session storage first, then Firestore if necessary.
 */
const fetchFieldsMetadata = useCallback(async () => {
//console.log(`[FieldMetadataContext: debug fetchFieldsMetadata] Triggered for formId: ${formId}`);

    if (!formId) {
        console.warn("[FieldMetadataContext] No formId provided, skipping metadata fetch.");
        return;
    }

    setIsLoading(true);

    // Generate storage keys for metadata and timestamps (mode-aware)
    // NOTE: metadataKey/lastUpdatedKey are useMemo() values above (can be null if no formId)
    const activeMetadataKey = metadataKey || `form_${formId}_fieldsMetadata__published`;
    const activeLastUpdatedKey = lastUpdatedKey || `form_${formId}_metadataLastUpdated__published`;

    // Legacy keys (pre-mode-aware) for fallback read/migration
    const legacyKey = legacyMetadataKey || `form_${formId}_fieldsMetadata`;
    const legacyUpdatedKey = legacyLastUpdatedKey || `form_${formId}_metadataLastUpdated`;


    // Step 1: Try to retrieve stored metadata from session/local storage
//    console.log(`[FieldMetadataContext: debug fetchFieldsMetadata] Checking storage for metadata using key: ${metadataKey}`);

    // Prefer mode-aware cache; fall back to legacy if needed
    const storedMetadata =
      safeGetItem(activeMetadataKey) ||
      safeGetItem(legacyKey);

if (storedMetadata) {
    console.log(`[FieldMetadataContext: debug fetchFieldsMetadata] Stored metadata retrieved for FormPreview (before validation):`, storedMetadata);
}
    const localLastUpdated =
      safeGetItem(activeLastUpdatedKey) ||
      safeGetItem(legacyUpdatedKey); // Local last updated timestamp

    if (storedMetadata && Object.keys(storedMetadata).length > 0) {
//        console.log("[FieldMetadataContext: debug fetchFieldsMetadata] Found metadata in storage. Validating metadata...");

        // Validate and normalize stored metadata before it reaches the canvas.
        // This preserves existing header cleanup and repairs legacy image fields.
        const validatedMetadata = normalizeMetadataForContext(storedMetadata);

        setFieldsMetadata(validatedMetadata);

        if (metadataKey) {
          safeSetItem(metadataKey, validatedMetadata);
        }

        // ────────────────────────────────────────────────────────────
        // Builder/public-preview: allow storage to short-circuit,
        // Viewer (/forms/:formId): DO NOT return here – fall through
        // so Firestore can refresh stale metadata.
        // ────────────────────────────────────────────────────────────
        if (isBuilderRoute || isPublicPreviewMode) {
          setIsLoading(false);
          return; // ✅ Use stored metadata, skip fetching from Firestore (builder only)
        }
        // Viewer: keep isLoading=true and continue to Firestore fetch
    }


    // Step 2: If no stored metadata, fetch from Firestore
//    console.log("[FieldMetadataContext: debug fetchFieldsMetadata] No metadata found in storage. Fetching from Firestore...");

    try {
        const formRef = doc(db, 'forms', formId);
        const formDoc = await getDoc(formRef);

        if (!formDoc.exists()) {
            console.warn(`[FieldMetadataContext: debug fetchFieldsMetadata] No metadata found in Firestore for formId: ${formId}`);
            setIsLoading(false);
            return;
        }

        const formData = formDoc.data() || {};

        // ────────────────────────────────────────────────────────────
        // NEW: version-aware resolve for fieldsMetadata
        // Builder/public-preview: viewMode 'auto' (overlay if present)
        // Viewer (/forms/:formId): viewMode 'published' (root only)
        // ────────────────────────────────────────────────────────────
        const viewMode = (isBuilderRoute || isPublicPreviewMode) ? effectiveViewMode : 'published';

        const versionSnap = getFormVersionSnapshot(formData);
        const resolvedMeta = resolveFieldsMetadata(formData, { viewMode, debug: true });

        console.log('[FieldMetadataContext: debug fetchFieldsMetadata] version snapshot:', versionSnap);
        console.log('[FieldMetadataContext: debug fetchFieldsMetadata] resolved fieldsMetadata source:', resolvedMeta);

        const compressedMetadata = resolvedMeta?.value;

        // Use an updated timestamp that matches the source we chose
        // - overlay: draftOverlaySavedAt / draftOverlay.savedAt
        // - root: lastModified
        const firestoreLastUpdated = (() => {
          try {
            if (resolvedMeta?.source === 'draftOverlay') {
              const t =
                formData?.draftOverlaySavedAt ||
                formData?.draftOverlay?.savedAt ||
                null;
              if (t && typeof t.toDate === 'function') return t.toDate().toISOString();
              if (t instanceof Date) return t.toISOString();
            }
            const lm = formData?.lastModified;
            if (lm && typeof lm.toDate === 'function') return lm.toDate().toISOString();
            if (lm instanceof Date) return lm.toISOString();
          } catch (e) {}
          return null;
        })();

        if (!compressedMetadata) {
            console.warn(`[FieldMetadataContext: debug fetchFieldsMetadata] No resolved metadata blob found for formId: ${formId} (viewMode=${viewMode})`);
            setIsLoading(false);
            return;
        }


        // Timestamp Validation: If local data is newer, skip Firestore overwrite
        // BUT ONLY in builder / public-preview. Viewer should always trust Firestore.
        if (
          (isBuilderRoute || isPublicPreviewMode) &&
          localLastUpdated &&
          firestoreLastUpdated &&
          localLastUpdated > firestoreLastUpdated
        ) {
            console.warn(
              `[FieldMetadataContext: debug fetchFieldsMetadata] Local metadata is newer than Firestore. Skipping Firestore overwrite.`
            );
            setIsLoading(false);
            return;
        }

        // Decompress Firestore metadata
//        console.log(`[FieldMetadataContext: debug fetchFieldsMetadata] Decompressing Firestore metadata for formId: ${formId}...`);
        const decompressedMetadata =
          (typeof compressedMetadata === 'string')
            ? await decompressData(compressedMetadata)
            : compressedMetadata;

//        console.log(`[FieldMetadataContext: debug fetchFieldsMetadata] Successfully decompressed metadata for formId: ${formId}.`);

        // Validate and normalize Firestore metadata before it reaches the canvas.
        // This preserves existing header cleanup and repairs legacy image fields.
        const validatedMetadata = normalizeMetadataForContext(
          decompressedMetadata,
        );


        // ✅ Store decompressed metadata in state
        setFieldsMetadata(validatedMetadata);
//        console.log(`[FieldMetadataContext: debug fetchFieldsMetadata] Final validated metadata (from Firestore) for FormPreview:`, validatedMetadata);

        //  Cache decompressed metadata in storage for optimized access
        safeSetItem(activeMetadataKey, validatedMetadata);

        //  Store Firestore's last updated timestamp
        safeSetItem(activeLastUpdatedKey, firestoreLastUpdated || new Date().toISOString());

    } catch (err) {
        console.error("[FieldMetadataContext: debug fetchFieldsMetadata] Error fetching metadata from Firestore:", err);
        setError(err.message);
    } finally {
        setIsLoading(false);
    }
}, [
  formId,
  isBuilderRoute,
  isPublicPreviewMode,
  effectiveViewMode,
  metadataKey,
  lastUpdatedKey,
  legacyMetadataKey,
  legacyLastUpdatedKey,
  normalizeMetadataForContext,
]);

useEffect(() => {
  // 1) This effect runs whenever formIdReady and formId are set.
//  console.log(
//    `[FieldMetadataContext: debug] useEffect triggered | formIdReady: ${formIdReady} | formId: ${formId} | isLoading: ${isLoading}`
//  );

  if (formIdReady && formId) {
//    console.log(
//      `[FieldMetadataContext: debug] Conditions met - checking for stored metadata for formId: ${formId}`
//    );

    const storedMetadata =
      (metadataKey ? safeGetItem(metadataKey) : null) ||
      safeGetItem(`form_${formId}_fieldsMetadata__${effectiveViewMode}`) ||
      safeGetItem(`form_${formId}_fieldsMetadata`);
    if (storedMetadata) {
      // 2) If we find cached metadata, we set it directly here:
//      console.log(
//        `[FieldMetadataContext: debug] Found cached metadata for formId: ${formId}. Data:`,
//        storedMetadata
//      );

      setFieldsMetadata(storedMetadata);
//      console.log(
//        `[FieldMetadataContext: debug] Just called setFieldsMetadata with stored data. fieldsMetadata is now being updated in state...`
//      );

      // ──────────────────────────────────────────────────────────
      // Builder/public-preview: cache is authoritative until Firestore
      // says otherwise (via fetchFieldsMetadata timestamp checks).
      // Viewer (/forms/:formId): do NOT stop here – still call
      // fetchFieldsMetadata so AUF can refresh from Firestore.
      // ──────────────────────────────────────────────────────────
      if (isBuilderRoute || isPublicPreviewMode) {
        setIsLoading(false);
        return;
      }
    }

    // 3) If nothing is in storage, OR we are on a viewer route,
    // we call fetchFieldsMetadata which will also do setFieldsMetadata
    // once it’s done.
    fetchFieldsMetadata();

  } else {
    console.warn(
      `[FieldMetadataContext: debug] formId not ready yet or formId is missing. Delaying fetch of metadata.`
    );
  }
}, [formIdReady, formId, fetchFieldsMetadata, isBuilderRoute, isPublicPreviewMode, metadataKey, effectiveViewMode]);

    // Fetch metadata for a single field
    const fetchSingleFieldMetadata = useCallback(async (fieldId) => {
        if (!fieldId) {
            console.error("[FieldMetadataContext: fetchSingleFieldMetadata] fetchSingleFieldMetadata called with no fieldId.");
            return;
        }

        console.log(`[FieldMetadataContext: fetchSingleFieldMetadata] Fetching metadata for single field with fieldId: ${fieldId}`);

        try {
            const response = await getFieldMetadataForSingleField(fieldId);

            if (response.status === 'success') {
                const fieldData = response.data;
                console.log(`[FieldMetadataContext: fetchSingleFieldMetadata] Raw Firestore metadata for fieldId ${fieldId}:`, fieldData);

                // ✅ Updated Header Validation Logic (Aligned with New Schema)
                if (fieldData.type === 'header') {
                    console.log(`[FieldMetadataContext: fetchSingleFieldMetadata] Pre-validation header metadata for fieldId ${fieldId}:`, fieldData);

                    fieldData.header = fieldData.header || "New Header Text";
                    fieldData.enableSubheader = 
                        fieldData.enableSubheader !== undefined
                            ? fieldData.enableSubheader
                            : false;

                    fieldData.subheader = fieldData.subheader || "";

                    fieldData.headerStyle = {
                        ...fieldData.headerStyle,
                        fontSize: fieldData.headerStyle?.fontSize || "24px",
                        headerColor: fieldData.headerStyle?.headerColor || "#000000",
                        textAlign: fieldData.headerStyle?.textAlign || "center",
                        fontWeight: fieldData.headerStyle?.fontWeight || "normal",
                        margin: fieldData.headerStyle?.margin || "0px 0",
                        padding: fieldData.headerStyle?.padding || "0px 0",
                    };

                    fieldData.subheaderStyle = {
                        ...fieldData.subheaderStyle,
                        subFontSize: fieldData.subheaderStyle?.subFontSize || "16px",
                        subheaderColor: fieldData.subheaderStyle?.subheaderColor || "#666666",
                        subheaderFontWeight: fieldData.subheaderStyle?.subheaderFontWeight || "normal",
                        textAlign: fieldData.subheaderStyle?.textAlign || "center",
                        margin: fieldData.subheaderStyle?.margin || "0px 0",
                        padding: fieldData.subheaderStyle?.padding || "0px 0",
                    };

                    // 🚨 Remove outdated `.style` property to ensure alignment with new schema
                    if (fieldData.style) {
                        console.warn(`[FieldMetadataContext: fetchSingleFieldMetadata] Removing outdated 'style' property for header field ID: ${fieldId}`);
                        delete fieldData.style;
                    }

                    console.log(`[FieldMetadataContext: fetchSingleFieldMetadata] Final validated header metadata for fieldId ${fieldId}:`, fieldData);
                }

                // Persist Updated Metadata in State + Persist to storage using the SAME "next" object
                setFieldsMetadata((prev) => {
                  const next = { ...prev, [fieldId]: fieldData };

                  safeSetItem(
                    (metadataKey || `form_${formId}_fieldsMetadata__${effectiveViewMode}`),
                    next
                  );

                  return next;
                });

                console.log(`[FieldMetadataContext: fetchSingleFieldMetadata] Successfully updated metadata for fieldId ${fieldId}.`);

            } else if (response.status === 'not_found') {
                console.warn(`[FieldMetadataContext] No metadata found for field ID: ${fieldId}`);
            }
        } catch (error) {
            console.error(`[FieldMetadataContext] Error fetching metadata for field ID: ${fieldId}`, error);
        }
    }, [formId, metadataKey, effectiveViewMode]);


// ---------------------------------------------------------------------------
// UPDATE THIS ENTIRE FUNCTION – immutability guarantees + stricter change
// detection for allowedImageTypes.  All previous header / image handling
// and logs are preserved verbatim.
// ---------------------------------------------------------------------------
const updateFieldMetadata = useCallback((fieldId, updates) => {
  setFieldsMetadata(prevMetadata => {
    const currentFieldData = prevMetadata[fieldId] ?? {};

    /* ────────────────────────────────────────────────────────────────
     * 0)  Fast-exit guard: no updates supplied
     * ──────────────────────────────────────────────────────────────── */
    if (!updates || Object.keys(updates).length === 0) {
      console.warn(
        `[updateFieldMetadata] No updates provided for field ID: ${fieldId}, skipping update.`,
      );
      return prevMetadata;                        // ← same ref is fine here
    }

    /* ────────────────────────────────────────────────────────────────
     * 1)  Change-detection helpers
     * ──────────────────────────────────────────────────────────────── */
    const hasStyleChanges =
      currentFieldData.type === 'header' &&
      (
        (updates.headerStyle     && !shallowEqual(updates.headerStyle,     currentFieldData.headerStyle)) ||
        (updates.subheaderStyle  && !shallowEqual(updates.subheaderStyle,  currentFieldData.subheaderStyle))
      );

    /* ⋯⋯ image-specific checks kept verbatim ⋯⋯ */
    let assetReferenceChanged = false;
    let imageTypesChanged     = false;

    if (currentFieldData.type === 'image' || updates.type === 'image') {
      /* — assetReference — */
      if (Object.prototype.hasOwnProperty.call(updates, 'assetReference')) {
        const curr = currentFieldData.assetReference ?? null;
        const next = updates.assetReference ?? null;

        assetReferenceChanged = !areAssetReferencesEqual(curr, next);

        if (assetReferenceChanged) {
          console.log(
            `[updateFieldMetadata] assetReference changed for field ID: ${fieldId}`,
            { old: curr, new: next },
          );
        }
      }

      /* — allowedImageTypes — */
      if (updates.allowedImageTypes) {
        const currArr = currentFieldData.allowedImageTypes ?? [];
        const nextArr = updates.allowedImageTypes;

        imageTypesChanged =
          !Array.isArray(currArr) ||
          currArr.length !== nextArr.length ||
          nextArr.some((t, i) => t !== currArr[i]);

        if (imageTypesChanged) {
          console.log(
            `[updateFieldMetadata] allowedImageTypes changed for field ID: ${fieldId}`,
            { old: currArr, new: nextArr },
          );
        }
      }

      /* log thumbnail swap */
      if (updates.thumbnail && currentFieldData.thumbnail !== updates.thumbnail) {
        console.log(
          `[updateFieldMetadata] Thumbnail updated for field ID: ${fieldId}:`,
          updates.thumbnail,
        );
      }
    }

    /* — basic shallow tests for *all other* props (now **includes** allowedImageTypes) — */
    const hasOtherChanges = Object.keys(updates).some(
      key => currentFieldData[key] !== updates[key] &&
             key !== 'headerStyle'      &&
             key !== 'subheaderStyle'   &&
             key !== 'assetReference',  // leave asset & types to specialised flags
    );

    const shouldUpdate =
      hasStyleChanges ||
      hasOtherChanges ||
      assetReferenceChanged ||
      imageTypesChanged;

    if (!shouldUpdate) {
//      console.log(
//        `[updateFieldMetadata] No material changes for field ID: ${fieldId}, skipping state update.`,
//      );
      return prevMetadata;                          // nothing changed
    }

    /* ────────────────────────────────────────────────────────────────
     * 2)  Build brand-new field object (never mutate currentFieldData)
     * ──────────────────────────────────────────────────────────────── */
    let updatedFieldData = {
      ...currentFieldData,
      ...updates,
      ufi: currentFieldData.ufi ?? updates.ufi,   // preserve original UFI
    };

    if (updatedFieldData.type === 'image') {
      updatedFieldData = normalizeImageFieldMetadata(updatedFieldData);
    }

    /* —★ header schema normalisation remains exactly as before — */
    if (currentFieldData.type === 'header') {
      updatedFieldData.header           = updatedFieldData.header          || 'New Header Text';
      updatedFieldData.enableSubheader  = updatedFieldData.enableSubheader !== undefined
                                             ? updatedFieldData.enableSubheader
                                             : false;
      updatedFieldData.subheader        = updatedFieldData.subheader       || '';

      updatedFieldData.headerStyle = {
        ...updatedFieldData.headerStyle,
        fontSize   : updatedFieldData.headerStyle?.fontSize    || '24px',
        headerColor: updatedFieldData.headerStyle?.headerColor || '#000000',
        textAlign  : updatedFieldData.headerStyle?.textAlign   || 'center',
        fontWeight : updatedFieldData.headerStyle?.fontWeight  || 'normal',
        margin     : updatedFieldData.headerStyle?.margin      || '0px 0',
        padding    : updatedFieldData.headerStyle?.padding     || '0px 0',
      };

      updatedFieldData.subheaderStyle = {
        ...updatedFieldData.subheaderStyle,
        subFontSize        : updatedFieldData.subheaderStyle?.subFontSize        || '16px',
        subheaderColor     : updatedFieldData.subheaderStyle?.subheaderColor     || '#666666',
        subheaderFontWeight: updatedFieldData.subheaderStyle?.subheaderFontWeight|| 'normal',
        textAlign          : updatedFieldData.subheaderStyle?.textAlign          || 'center',
        margin             : updatedFieldData.subheaderStyle?.margin             || '0px 0',
        padding            : updatedFieldData.subheaderStyle?.padding            || '0px 0',
      };
    }

    /* ────────────────────────────────────────────────────────────────
     * 3)  Return a *brand-new* metadata map (triggers React context
     *     update → PreviewFields repaint)
     * ──────────────────────────────────────────────────────────────── */
    const nextMetadata = {
      ...prevMetadata,                  // new wrapper object
      [fieldId]: updatedFieldData,      // new inner object
    };

    console.log(
      `[updateFieldMetadata] Metadata updated for field ID: ${fieldId}`,
      updatedFieldData,
    );

    // persist
    safeSetItem((metadataKey || `form_${formId}_fieldsMetadata__${effectiveViewMode}`), nextMetadata);

    return nextMetadata;                // ← **new reference**
  });
}, [formId, metadataKey, effectiveViewMode]);


  /**
   * addOrUpdateCalculated ─ create or overwrite a calculated-field
   * definition inside fieldsMetadata and persist it to storage.
   *
   * @param {string} ufi   – stable key for the calc field
   * @param {object} meta  – { label, formula, delimiter, template, … }
   */
  const addOrUpdateCalculated = useCallback((ufi, meta = {}) => {
    if (!ufi) {
      console.error('[FieldMetadataContext] addOrUpdateCalculated called with empty UFI');
      return;
    }

    setFieldsMetadata(prev => {
      const next = {
        ...prev,
        [ufi]: {
          ...(prev[ufi] || {}),
          ...meta,               // overwrite / add new props
          ufi,
          type: 'calculated',    // enforce
        },
      };

      // persist alongside the rest of the metadata cache
        safeSetItem((metadataKey || `form_${formId}_fieldsMetadata__${effectiveViewMode}`), next);

      console.log('[FieldMetadataContext] Calculated-field metadata saved:', next[ufi]);
      return next;               // trigger context update
    });
}, [formId, metadataKey, effectiveViewMode]);


/**
 * Shallow equality check for detecting changes in headerStyle and subheaderStyle
 */
const shallowEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true; // Identical references
    if (!obj1 || !obj2) return false; // One of them is undefined/null
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;

    return keys1.every(key => obj1[key] === obj2[key]);
};



const addFieldMetadata = useCallback((fieldId, fieldType, existingUFI = null, existingOptionsSetUFI = null) => {
    // Prevent adding metadata if the metadata is still loading
if (isLoading && fieldsMetadata[fieldId]) {
  console.warn("[FieldMetadataContext: addFieldMetadata] Metadata is still loading and already exists for field ID:", fieldId);
  return; // Skip only if metadata for this field already exists
}


    // Ensure fieldId is provided
    if (!fieldId) {
        console.error("[FieldMetadataContext: addFieldMetadata] Invalid fieldId detected when adding metadata:", fieldId);
        return;
    }

    // Ensure fieldType is provided
    if (!fieldType) {
        console.error("[FieldMetadataContext: addFieldMetadata] Invalid fieldType provided for fieldId:", fieldType);
        return;
    }

//    console.log(`[FieldMetadataContext: addFieldMetadata] Adding new field metadata for field ID: ${fieldId} with type: ${fieldType}. Existing UFI: ${existingUFI}, Existing optionsSetUFI: ${existingOptionsSetUFI}`);

//    console.log(`[addFieldMetadata] Proceeding with fieldId: ${fieldId} and fieldType: ${fieldType}`);

    // Skip metadata creation for submit buttons
    if (fieldType === 'submit') {
//        console.log(`[FieldMetadataContext: addFieldMetadata] Skipping submit button metadata for field ID: ${fieldId}`);
        return;  // Early return to skip submit buttons
    }

    // Determine if this field type requires an optionsSetUFI
    const requiresOptionsSetUFI =
      fieldType === 'single-select' ||
      fieldType === 'multi-select' ||
      fieldType === 'asset-type';

    // Check if metadata for this field already exists and is fully initialized
    const existingMetadata = fieldsMetadata[fieldId];
    if (
        existingMetadata &&
        existingMetadata.ufi && 
        (!requiresOptionsSetUFI || existingMetadata.optionsSetUFI)
    ) {
//        console.log(`[FieldMetadataContext: addFieldMetadata] Metadata already exists for field ID ${fieldId}, skipping creation.`);
        return;  // Avoid adding metadata again if it already exists and meets requirements
    }

    // Ensure that we have a UFI (reuse if provided, generate if not)
const ufi = existingUFI || uuidv4();
//console.log(`[addFieldMetadata] Computed UFI for field ${fieldId}:`, ufi);

//    const ufi = existingUFI || null;  
//console.log("[addFieldMetadata] Received existing UFI:", existingUFI, "for Field ID:", fieldId);

    // Ensure that we have an optionsSetUFI (reuse if provided, generate if not)
    const optionsSetUFI = requiresOptionsSetUFI ? (existingOptionsSetUFI || uuidv4()) : null;
//    console.log(`[FieldMetadataContext: addFieldMetadata] Reusing or generating new UFI: ${ufi}${requiresOptionsSetUFI ? `, and optionsSetUFI: ${optionsSetUFI}` : ''} for field ID: ${fieldId}`);

    // Fetch default metadata using the fieldId and pass the UFI and optionsSetUFI
    const defaultMetadata = getDefaultFieldMetadata(fieldId, ufi, fieldType, optionsSetUFI);  // Ensure fieldType is passed to get correct metadata
    const metadataForField = defaultMetadata[fieldId] || {};  // Use fieldId as key for metadata

//console.log(`[addFieldMetadata] Default metadata for field ${fieldId}:`, metadataForField);

    // ✅ Updated Header Logic (New Schema Alignment)
    if (fieldType === 'header') {
        metadataForField.header = metadataForField.header || "New Header Text";
        metadataForField.enableSubheader = 
            metadataForField.enableSubheader !== undefined 
                ? metadataForField.enableSubheader
                : false;
        metadataForField.subheader = metadataForField.subheader || "";

        metadataForField.headerStyle = {
            ...metadataForField.headerStyle,
            fontSize: metadataForField.headerStyle?.fontSize || "24px",
            headerColor: metadataForField.headerStyle?.headerColor || "#000000",
            textAlign: metadataForField.headerStyle?.textAlign || "center",
            fontWeight: metadataForField.headerStyle?.fontWeight || "normal",
            margin: metadataForField.headerStyle?.margin || "0px 0",
            padding: metadataForField.headerStyle?.padding || "0px 0",
        };

        metadataForField.subheaderStyle = {
            ...metadataForField.subheaderStyle,
            subFontSize: metadataForField.subheaderStyle?.subFontSize || "16px",
            subheaderColor: metadataForField.subheaderStyle?.subheaderColor || "#666666",
            subheaderFontWeight: metadataForField.subheaderStyle?.subheaderFontWeight || "normal",
            textAlign: metadataForField.subheaderStyle?.textAlign || "center",
            margin: metadataForField.subheaderStyle?.margin || "0px 0",
            padding: metadataForField.subheaderStyle?.padding || "0px 0",
        };
    }



    // Add specific handling for `image` fields
    if (fieldType === 'image') {
//        console.log(`[FieldMetadataContext: addFieldMetadata] Initializing metadata for image field ID ${fieldId}.`);
        metadataForField.assetReference = null; // Placeholder for DAM-lite integration (assetId will be assigned upon upload)
        metadataForField.allowedImageTypes = ['icon']; // Default allowed image types
        metadataForField.style = {
            ...metadataForField.style, // Merge any existing styles
            width: '100%', // Default full width
            height: 'auto', // Maintain aspect ratio
        };
    }

    // Update the fieldsMetadata state with the new field's metadata
    setFieldsMetadata((prevMetadata) => {
        const updatedMetadata = {
            ...prevMetadata,
            [fieldId]: {
                ...metadataForField,  // Merge in default metadata for the field
                id  : fieldId,        // Ensure the fieldId is set
                // Preserve normalized type from getDefaultFieldMetadata
                // (e.g. asset-type → single-select)
                type: metadataForField.type || fieldType,
                ufi : metadataForField.ufi,  // Use the UFI from default metadata
                optionsSetUFI: requiresOptionsSetUFI
                  ? optionsSetUFI
                  : undefined,       // Set optionsSetUFI only if required
                style: {
                    ...metadataForField.style,  // Merge any existing styles
                    marginLeft : '6px',
                    marginRight: '6px',
                    paddingTop : '2px',
                    paddingBottom: '4px',
                },
            },
        };


//        console.log(`[FieldMetadataContext: addFieldMetadata] Final metadata for field ID ${fieldId}:`, updatedMetadata[fieldId]);

        // Persist the updated metadata to local/session storage for form persistence
        safeSetItem((metadataKey || `form_${formId}_fieldsMetadata__${effectiveViewMode}`), updatedMetadata);


        // Return the updated metadata object
        return updatedMetadata;
    });
}, [formId, fieldsMetadata, isLoading, metadataKey, effectiveViewMode]);


    // Delete metadata for a specific field
    const deleteFieldMetadata = useCallback((fieldId) => {
        setFieldsMetadata((prevMetadata) => {
            const updatedMetadata = { ...prevMetadata };
            delete updatedMetadata[fieldId]; // Remove the field's metadata

            console.log(`[FieldMetadataContext] Deleted metadata for field ID: ${fieldId}`);

            // Persist updated metadata to storage
            safeSetItem((metadataKey || `form_${formId}_fieldsMetadata__${effectiveViewMode}`), updatedMetadata);
            return updatedMetadata;
        });
    }, [formId, metadataKey, effectiveViewMode]);

    // Memoize context value to avoid re-renders unless dependencies change
   const contextValue = useMemo(() => ({
     fieldsMetadata,
     isLoading,
     error,
     fetchSingleFieldMetadata,
     updateFieldMetadata,
     addFieldMetadata,
     deleteFieldMetadata,
    addOrUpdateCalculated,
   }), [
     fieldsMetadata,
     isLoading,
     error,
     fetchSingleFieldMetadata,
     updateFieldMetadata,
     addFieldMetadata,
     deleteFieldMetadata,
    addOrUpdateCalculated,
   ]);


    return (
        <FieldMetadataContext.Provider value={contextValue}>
            {children}
        </FieldMetadataContext.Provider>
    );
};
